import {
  ReactNode,
  createContext,
  useContext,
  useEffect,
  useState,
} from "react";

import { getGlobalSettings, setGlobalSettings } from "~/helpers/globalSettings";

type Theme = "light" | "dark";
export type ThemePreference = "light" | "dark" | "system";

interface ThemeContextType {
  theme: Theme;
  themePreference: ThemePreference;
  setTheme: (theme: Theme) => void;
  setThemePreference: (preference: ThemePreference) => void;
  toggleTheme: () => void;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error("useTheme must be used within a ThemeProvider");
  }

  return context;
};

interface ThemeProviderProps {
  children: ReactNode;
}

const getSystemTheme = (): Theme => {
  if (typeof window === "undefined") return "light";

  return window.matchMedia("(prefers-color-scheme: dark)").matches
    ? "dark"
    : "light";
};

const setDataTheme = (value: string) => {
  document.documentElement.setAttribute("data-theme", value);
};

export const ThemeProvider = ({ children }: ThemeProviderProps) => {
  const [themePreference, setThemePreferenceState] = useState<ThemePreference>(
    () => {
      if (typeof window === "undefined") return "system";

      const gs = getGlobalSettings();

      return gs.themePreference || "system";
    },
  );

  const [theme, setThemeState] = useState<Theme>(() => {
    if (typeof window === "undefined") return "light";

    const gs = getGlobalSettings();
    const pref = gs.themePreference || "system";

    if (pref === "system") {
      return getSystemTheme();
    }

    return pref as Theme;
  });

  const setTheme = (newTheme: Theme) => {
    setThemeState(newTheme);
    setThemePreferenceState(newTheme);
    setGlobalSettings({ themePreference: newTheme });
    setDataTheme(newTheme);
  };

  const setThemePreference = (preference: ThemePreference) => {
    setThemePreferenceState(preference);
    setGlobalSettings({ themePreference: preference });

    if (preference === "system") {
      const systemTheme = getSystemTheme();
      setThemeState(systemTheme);
      setDataTheme(systemTheme);
    } else {
      setThemeState(preference);
      setDataTheme(preference);
    }
  };

  const toggleTheme = () => {
    setTheme(theme === "light" ? "dark" : "light");
  };

  useEffect(() => {
    if (typeof window === "undefined") return;

    // Set initial theme on mount / update data-theme on theme change
    setDataTheme(theme);
  }, [theme]);

  useEffect(() => {
    if (typeof window === "undefined") return;

    // Listen for system theme changes and update if preference is set to system
    const mediaQuery = window.matchMedia("(prefers-color-scheme: dark)");

    const handleSystemThemeChange = (e: MediaQueryListEvent) => {
      // Only update if preference is set to system
      if (themePreference === "system") {
        const systemTheme = e.matches ? "dark" : "light";
        setThemeState(systemTheme);
        setDataTheme(systemTheme);
      }
    };

    mediaQuery.addEventListener("change", handleSystemThemeChange);

    return () => {
      mediaQuery.removeEventListener("change", handleSystemThemeChange);
    };
  }, [themePreference]);

  const value = {
    theme,
    themePreference,
    setTheme,
    setThemePreference,
    toggleTheme,
  };

  return (
    <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>
  );
};
